import {
  AfterViewInit, Component, Input, OnDestroy, OnChanges, SimpleChanges, ViewChild,
  signal
} from '@angular/core';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import {
  Subscription,
  Subject,
  Observable
} from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { PersonaProviderProfile, PersonaProviderStore } from '@sondermind/data-access/provider-facing/persona-provider';

import {
  CombinedClientAndClientReferralMember,
  ClientStatus,
  CLIENT_TYPES
} from '@sondermind/utilities/models-clinical';
import { BreakpointsService } from '@sondermind/utilities/services';

import { AasmState, ReferralSourceEnum } from '@sondermind/utilities/models-referrals';
import { IconSizes, IrisIconComponent } from '@sondermind/iris/icon';
import { IrisButtonModule } from '@sondermind/iris/button';
import { ReferralSourcePipe } from '@sondermind/utilities/pipes';
import { LocalizedDatePipe } from '@sondermind/utilities/time-and-date';
import { RequestedTimeSlotPipe } from '@sondermind/utilities/pipes/requested-time-slot.pipe';
import { TooltipModule } from '@sondermind/features/tooltip';
import { MatchClientStore } from '../../../stores/match-client.store';

@Component({
  selector: 'member-match-clients-table',
  templateUrl: './match-clients-table.component.html',
  styleUrls: ['./match-clients-table.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TooltipModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    IrisButtonModule,
    IrisIconComponent,
    ReferralSourcePipe,
    RequestedTimeSlotPipe,
    LocalizedDatePipe,
  ]
})
export class MatchClientsTable implements OnChanges, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('tabs', { static: false }) tabs: MatTabGroup;
  @Input() status: ClientStatus;
  @Input() reload: string;
  @Input() showOlder: boolean;
  @Input() timezone: string;
  @Input() showAvailabilityErrorState: boolean;

  referralSource = ReferralSourceEnum;
  aasmState = AasmState;

  readonly clients = signal<CombinedClientAndClientReferralMember[]>([]);
  clientDataSub: Subscription;
  loading: boolean;
  loadingData = 'Loading please wait ....';
  paginationData: any = {
    total: 0, filtered: 0, pages: 0, number: 1, size: 15
  };
  displayedColumns: string[] = [
    'last_name',
    'matched_at',
    'source',
    'preference',
    'topics',
    'requested_session',
    'next_session_datetime'
  ];

  showSessionDate: boolean = false;
  emptyClientStatusFilter: boolean = false;
  tableState = {
    page: 1,
    pageIndex: 0,
    pageSize: 15,
    sortBy: 'matched_at',
    sortAscending: false,
    sortDirection: 'desc',
    includeClientsRetainedMoreThan30Days: true
  };
  matchClientType = CLIENT_TYPES.match;
  clientStatus = ClientStatus;
  filterOptions = {
    filter: [
      { key: 'client_type', value: this.matchClientType },
    ],
    page: {
      size: this.tableState.pageSize,
      number: 1
    },
    sort: {
      key: this.tableState.sortBy,
      asc: this.tableState.sortAscending
    }
  };
  personaProviderProfile$: Observable<PersonaProviderProfile>;

  irisIconSizes = IconSizes;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public matchClientStore: MatchClientStore,
    public breakPoint: BreakpointsService,
    public providerStore: PersonaProviderStore
  ) { }

  private destroyedSubj: Subject<void> = new Subject<void>();
  destroyed$: Observable<void> = this.destroyedSubj.asObservable();

  ngOnChanges(changes: SimpleChanges): void {
    if (this.status === this.matchClientStore.status) {
      if (this.clientStatus.CLIENT) {
        this.showSessionDate = true;
      }

      if (this.clientDataSub && !changes.status?.firstChange && changes.reload && changes.reload.currentValue === this.status) {
        this.loadData();
      }

      if (!this.clientDataSub) {
        this.filterOptions.filter.push({ key: 'status', value: this.matchClientStore.status });

        this.matchClientStore.filters = this.filterOptions;
        this.initializeTable();
      }

      if (this.matchClientStore.status === this.clientStatus.ACCEPTED && changes.showOlder) {
        this.tableState.includeClientsRetainedMoreThan30Days = this.showOlder;
        this.loadData();
        this.matchClientStore.loadMatchClientAnalytics();
      }
    }

    this.personaProviderProfile$ = this.providerStore.getPersonaProviderProfile$();
  }

  ngAfterViewInit(): void {
    this.sort.sortChange.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((sorting: Sort) => {
      // Update sort state based on MatSort direction
      this.tableState.sortDirection = sorting.direction;
      this.tableState.sortAscending = sorting.direction === 'asc';
      this.tableState.sortBy = sorting.active;

      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
      this.tableState.page = 1;
      this.tableState.pageIndex = 0;
      this.matchClientStore.updateTableState(this.tableState);
      this.loadData();
    });
  }

  ngOnDestroy(): void {
    this.destroyedSubj.next();
    this.destroyedSubj.complete();
  }

  initializeTable(): void {
    this.matchClientStore.tableState$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((res) => {
      if (res && res[this.status] !== this.tableState && this.status === this.matchClientStore.status) {
        this.tableState = res[this.status];
        if (this.sort) {
          this.sort.direction = this.tableState.sortAscending ? 'asc' : 'desc';
        }
        this.loadData();
      }
    });

    this.route.data.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((data) => {
      if (data.statuses) {
        this.loadData();
      }
    });

    this.clientDataSub = this.matchClientStore.clientData$
      .pipe(
        takeUntil(this.destroyed$),
      ).subscribe((response) => {
        if (response.data) {
          this.paginationData = { ...response.meta?.counts, ...response.meta?.page };
          // response data sorted by newest clients first
          this.clients.set(response.data || []);
          this.loading = false;
          this.loadingData = '';
          this.emptyClientStatusFilter = false;
          this.matchClientStore.initialLoadComplete = true;
        }
      });
  }

  onPaginate(event): void {
    this.tableState.page = event.pageIndex + 1;
    this.tableState.pageIndex = event.pageIndex;
    this.tableState.pageSize = event.pageSize;
    this.matchClientStore.updateTableState(this.tableState);
    this.clients.set([]);
    this.loadData();
  }

  loadData(): void {
    this.loadingData = 'Loading, please wait ...';
    this.loading = true;

    this.filterOptions = {
      filter: [
        { key: 'client_type', value: this.matchClientType },
        { key: 'status', value: this.matchClientStore.status }
      ],
      page: {
        size: this.tableState.pageSize,
        number: this.tableState.page
      },
      sort: {
        key: this.tableState.sortBy,
        asc: this.tableState.sortAscending
      }
    };

    if (this.status === this.clientStatus.ACCEPTED) {
      if (!this.tableState.includeClientsRetainedMoreThan30Days) {
        this.filterOptions.filter.push({ key: 'clients_newer_than_days', value: '30' });
      } else {
        const index = this.filterOptions.filter.indexOf(this.filterOptions.filter.find((filter) => filter.key === 'clients_newer_than_days'));
        if (index !== -1) {
          this.filterOptions.filter.splice(index, 1);
        }
      }
    }

    if (this.status === this.clientStatus.CLIENT) {
      this.filterOptions.filter.push({ key: 'exclude_inactive_members', value: 'true' });
    }

    this.matchClientStore.filters = this.filterOptions;
    this.matchClientStore.loadClients();
  }

  viewClient(patient: CombinedClientAndClientReferralMember): void {
    // eslint-disable-next-line no-nested-ternary
    const type = patient.status === this.clientStatus.CLIENT
      ? 'match'
      : patient.status === this.clientStatus.ACCEPTED
        ? 'accepted'
        : 'new_match';

    if (type === 'match') {
      this.router.navigate(['clients', patient.id], { state: { type: 'match' } });
    } else if (patient.status === this.clientStatus.ACCEPTED) {
      this.router.navigate(['referrals', patient.id], { state: { type: 'accepted' } });
    } else {
      this.router.navigate(['referrals', patient.id], { state: { type: 'new_match' } });
    }
  }

  goToCalendarSession(event: PointerEvent, eventId: number): void {
    event.stopPropagation();
    this.router.navigate(['scheduling', 'events', eventId]);
  }
}
